import React from "react";

import {
  StyleSheet,
  TouchableOpacity,
  View,
  Dimensions,
  Text,
  Platform,
} from "react-native";

import GestureRecognizer, {
  swipeDirections,
} from "react-native-swipe-gestures";

import moment from "moment";

import Animation from "./LotieView";

import AppConfig from "../../../config/AppConfig";
import {
  isAndroid,
  isAppleDevice,
  isWeb,
  navigate,
  setStorageItem,
} from "../../../helpers/utils";
import CarouselProgressBar from "./progressBar/CarouselProgressBar";
import OnboardingButtons from "./OnboardingButtons";
import LoadingIndicator from "../common/LoadingIndicator";

import screens from "./data";
import appStyles from "../../appStyles";
import appPages from "../../../appPages";
import { INTEGRATION_KEYS } from "../integrations/IntegrationsHelper";
import steps from "../integrations/onboarding/steps";

const { width: WIDTH, height: HEIGHT } = Dimensions.get("window");
const ONBOARDING_SCREENS = screens.length;
const isMobileWeb = isWeb && HEIGHT <= 820;

export default class OnboardingAnimPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.animation = null;

    this.state = {
      onboardingScreen: 0,
      animIdx: 0,
      isLoading: isWeb,
      data: null,
      metadata: {},
      isLoggedIn: !!this.controller.state?.currentUser,
    };

    this.startOnboardingProcess();
  }

  startOnboardingProcess = () => {
    setStorageItem("ONBOARDING_TIMER", moment().unix());
  };

  componentDidMount = () => {
    const { onboardingScreen } = this.state;

    if (onboardingScreen && onboardingScreen >= ONBOARDING_SCREENS) {
      this.setState({ onboardingScreen: 0, animIdx: 0, data: null });
    }

    this.preload();
    this.retrieveDataIfNeeded();

    if (!isWeb) {
      this.startAnimation();
    }
  };

  componentDidUpdate = () => {
    this.retrieveDataIfNeeded();
  };

  preload() {
    for (var i = 0; i < ONBOARDING_SCREENS; i++) {
      const renderScreen = this.getCurrentScreen(i);
      if (isWeb) {
        for (var j in renderScreen.animation?.data) {
          fetch(renderScreen.animation?.data[j]);
        }
      }
    }
  }

  getCurrentScreen(onboardingScreen) {
    const renderScreen =
      screens[
        onboardingScreen >= ONBOARDING_SCREENS
          ? ONBOARDING_SCREENS - 1
          : onboardingScreen
      ];
    return renderScreen;
  }

  retrieveDataIfNeeded() {
    const { onboardingScreen, data, animIdx } = this.state;

    const renderScreen = this.getCurrentScreen(onboardingScreen);
    const { animation } = renderScreen;

    if (isWeb && animation != null && data == null) {
      const src = animation.data[animIdx];

      if (src != null) {
        fetch(src)
          .then((response) => response.json())
          .then((imageData) => {
            this.setState({
              isLoading: false,
              data: imageData,
              metadata: { ...renderScreen },
            });
            this.startAnimation();
          });
      }
    }
  }

  startAnimation() {
    setTimeout(() => {
      if (this.animation != null) this.animation.play();
    }, 300);
  }

  nextImage() {
    const { onboardingScreen, animIdx } = this.state;

    const renderScreen = this.getCurrentScreen(onboardingScreen);
    if (animIdx < renderScreen.animation?.data.length - 1) {
      this.setState({ animIdx: animIdx + 1, data: null });
    }
  }

  goHome = (isExit = false) => {
    const { isLoggedIn } = this.state;

    if (!isLoggedIn) {
      if (this.controller.hasNavigation()) {
        console.log("> Navigate: login");
        this.props.navigation.navigate("login");
      } else {
        this.controller.changePage("login");
      }
    } else {
      if (isExit) {
        this.controller.navigation.openDrawer();
      } else {
        const params = this.getNextUrl();
        navigate(this.controller, params.url, "changePage", {
          step: params.step,
        });
      }
    }
  };

  getNextUrl = () => {
    const devices = this.controller.state?.settings?.devices || {};

    // Check which integrations are not enabled to redirect to that page
    const integrations = {};
    Object.keys(devices).forEach((key) => {
      const data = devices[key] || [];
      data.forEach((d) => {
        if (d.isEnabled) integrations[key] = true;
      });
    });

    let url = appPages.integrationsOnboarding;
    let step = 0;

    if (!integrations["google_calendar"] || !integrations["mobile_calendar"]) {
      step = steps.calendar;
    } else if (isWeb && !integrations["desktop"]) {
      step = steps.desktop;
    } else if (isAppleDevice && !integrations[INTEGRATION_KEYS.health]) {
      step = steps.health;
    } else {
      url = appPages.customizeElementsPage;
    }

    return { url, step };
  };

  changeScreen(offset = 1) {
    const { onboardingScreen } = this.state;

    const next = onboardingScreen + offset;

    // Exit
    if (next >= ONBOARDING_SCREENS || next == -1) {
      this.goHome(next == -1);
    } else {
      this.setState({
        onboardingScreen: next,
        animIdx: 0,
        data: null,
      });

      if (!isWeb) {
        this.startAnimation();
      }
    }
  }

  onSwipe = (direction) => {
    const { SWIPE_LEFT, SWIPE_RIGHT } = swipeDirections;

    const offset =
      direction == SWIPE_LEFT ? 1 : direction == SWIPE_RIGHT ? -1 : 0;
    this.changeScreen(offset);
  };

  render() {
    const { isLoading, onboardingScreen, animIdx, data, metadata, isLoggedIn } =
      this.state;

    const renderScreen = this.getCurrentScreen(onboardingScreen);
    const isLastSlide = onboardingScreen == ONBOARDING_SCREENS - 1;

    const animStyle = {
      width: isWeb ? Math.min(WIDTH + 10, AppConfig.MAX_WIDTH) : WIDTH + 10,
      maxHeight: "100%",
    };

    const src = isWeb ? data : renderScreen.animation?.data[animIdx];
    const extraData = isWeb ? metadata : { ...renderScreen };
    const isLoop = renderScreen.animation?.loop[animIdx];

    const showButtons = isLastSlide || (!isLoggedIn && onboardingScreen == 0);

    return (
      <GestureRecognizer
        onSwipe={this.onSwipe}
        style={[
          appStyles.container,
          {
            paddingTop: isWeb ? 10 : 0,
          },
        ]}
      >
        {isLoading ? (
          <LoadingIndicator isLoading={isLoading} />
        ) : (
          <>
            <CarouselProgressBar
              currentScreenIndex={onboardingScreen}
              numberOfScreens={ONBOARDING_SCREENS - 1}
              changeScreen={(offset) => this.changeScreen(offset)}
              hideNext={onboardingScreen >= ONBOARDING_SCREENS - 1}
            />

            <View
              style={[
                styles.animationContainer,
                styles.fullscreen,
                extraData.animationContainerCss &&
                  styles[extraData.animationContainerCss],
              ]}
            >
              {extraData.title ? (
                <Text
                  selectable={false}
                  onPress={() => this.changeScreen()}
                  style={[
                    styles.text,
                    styles.title,
                    extraData.titleCss && styles[extraData.titleCss],
                  ]}
                >
                  {extraData.title}
                </Text>
              ) : null}

              <TouchableOpacity
                onPress={
                  !isLoggedIn && onboardingScreen == 0 // dont allow image tap for 1st screen
                    ? null
                    : () => this.changeScreen()
                }
                style={{
                  maxHeight: "100%",
                }}
              >
                {src ? (
                  <Animation
                    key={onboardingScreen}
                    ref={(ref) => {
                      this.animation = ref;
                    }}
                    style={[
                      animStyle,
                      extraData.animationCss && styles[extraData.animationCss],
                    ]}
                    source={src}
                    loop={isLoop}
                    autoplay={false}
                    onAnimationFinish={() => this.nextImage()}
                  />
                ) : null}
              </TouchableOpacity>

              {!isLoggedIn || onboardingScreen != 0 ? (
                <View
                  style={[
                    styles.bottomContainer,
                    onboardingScreen == 0 && { bottom: 150 },
                    isLastSlide && { bottom: 120 },
                  ]}
                >
                  {extraData.subTitle ? (
                    <Text
                      selectable={false}
                      style={[
                        styles.text,
                        styles.subTitle,
                        extraData.subTitleCss && styles[extraData.subTitleCss],
                      ]}
                    >
                      {extraData.subTitle}
                    </Text>
                  ) : null}
                </View>
              ) : null}

              {!showButtons ? null : (
                <OnboardingButtons
                  isLastSlide={isLastSlide}
                  onboardingScreen={onboardingScreen}
                  goHome={this.goHome}
                  changeScreen={(offset) => this.changeScreen(offset)}
                />
              )}
            </View>
          </>
        )}
      </GestureRecognizer>
    );
  }
}

const styles = StyleSheet.create({
  /* Images */
  animationContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  fullscreen: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  },
  imageBottom: {
    maxHeight: "100%",
  },
  moveY70: {
    transform: [{ translateY: 90 }],
  },
  imageContentBottom: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    top: "10%",
    height: "90%",
  },
  imageContentEnd: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  image95: {
    maxHeight: isMobileWeb ? "80%" : "95%",
    marginTop: isMobileWeb ? "20%" : "5%",
  },
  imageMindful: {
    maxHeight: isMobileWeb ? 230 : isAndroid ? "70%" : "95%",
    marginTop: isMobileWeb ? 0 : isAndroid ? "18%" : 0,
  },
  bottomContainer: {
    position: "absolute",
    left: 0,
    bottom: 20,
    right: 0,
  },
  text: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 32,
    lineHeight: 35,
    textAlign: "center",
    letterSpacing: 0.5,
    color: "#000000",
    zIndex: 10,
  },
  title: {
    position: "absolute",
    left: 0,
    top: 100,
    right: 0,
  },
  subTitle: {
    marginBottom: 15,
  },
  textTopLeft: {
    fontSize: 32,
    lineHeight: 35,
    position: "absolute",
    top: "25%",
    left: "-25%",
  },
  textBottomRight: {
    fontSize: 26,
    lineHeight: 35,
    position: "absolute",
    right: "10%",
    bottom: "10%",
  },
  textBottomCenter: {
    fontSize: 32,
    lineHeight: 35,
    letterSpacing: 0.5,
    position: "absolute",
    right: "20%",
    bottom: "10%",
  },
  relative: {
    position: "relative",
    top: 0,
    bottom: 0,
  },
  top50: {
    position: "absolute",
    top: 50,
  },
  subTitle1: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 26,
    lineHeight: 35,
    letterSpacing: 0.175,
    color: "#000",
  },
  imageFullscreen: {
    maxHeight: "200%",
  },
});
